import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { useSupabaseClient } from "@/lib/supabase";
import { toast } from "@/lib/toast";
import { useCreateNotification } from "@/features/notifications/hooks/useCreateNotification";
import { generateRoteiro, type GenerateRoteiroResult } from "../api/generateRoteiro";
import type { GenerateRoteiroInput } from "../types";

export function useGenerateRoteiro() {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const { client, isReady } = useSupabaseClient();
  const createNotification = useCreateNotification();

  return useMutation<GenerateRoteiroResult, Error, GenerateRoteiroInput>({
    mutationFn: async (input) => {
      const token = await session?.getToken();
      if (!token) throw new Error("Usuário não autenticado.");
      return generateRoteiro(input, token);
    },
    onSuccess: async (data, input) => {
      if (isReady) {
        const { error } = await client
          .from("ai_jobs")
          .update({
            request_metadata: {
              normalized: {
                topic: input.topic,
                type: input.type,
                objective: input.objetivo,
                details: input.details || null,
                generationKind: input.generationKind ?? "base",
                parentJobId: input.parentJobId ?? null,
                rootJobId: input.rootJobId ?? null,
              },
              source: "client-sync",
            },
          })
          .eq("id", data.jobId)
          .eq("task", "roteiros");

        if (error) {
          console.error("[roteiros] failed to persist request metadata:", error.message);
        }
      }

      void queryClient.invalidateQueries({ queryKey: ["ai_jobs", "roteiros"] });

      // User-facing completion signals: toast + sininho notification with
      // deep-link to the generated roteiro. Long-running AI jobs MUST always
      // notify, otherwise users abandon mid-wait. See conversation 2026-05-13.
      const href = `/roteiros/${data.jobId}`;
      const title = `Roteiro ${input.type} pronto`;
      toast.success(title, {
        description: input.topic,
        action: {
          label: "Abrir",
          onClick: () => { window.location.assign(href); },
        },
      });
      void createNotification({
        type: "roteiro_generated",
        title,
        body: input.topic,
        href,
        metadata: { jobId: data.jobId, type: input.type, objetivo: input.objetivo },
      });
    },
  });
}
